import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { trpc } from "../lib/trpc";
import { Button } from "./ui/button";

type RecordingCountProps = {
  isRecording: boolean;
  codigoBip: string | null;
  videoRef: React.RefObject<HTMLVideoElement | null>;
};

const MOTION_FRAME_WIDTH = 160;
const MOTION_FRAME_HEIGHT = 90;
const MOTION_ANALYSIS_INTERVAL_MS = 125;
const MOTION_PIXEL_THRESHOLD = 45;
const MOTION_RATIO_THRESHOLD = 0.07;
const MOTION_SETTLE_MS = 400;
const MIN_COUNT_INTERVAL_MS = 800;
const MAX_SAVE_RETRY_MS = 120000;

function calculateMotionRatio(current: Uint8ClampedArray, previous: Uint8ClampedArray): number {
  let changedPixels = 0;
  let sampledPixels = 0;
  for (let index = 0; index < current.length; index += 16) {
    const difference = Math.abs(current[index] - previous[index])
      + Math.abs(current[index + 1] - previous[index + 1])
      + Math.abs(current[index + 2] - previous[index + 2]);
    if (difference >= MOTION_PIXEL_THRESHOLD) changedPixels++;
    sampledPixels++;
  }
  return sampledPixels > 0 ? changedPixels / sampledPixels : 0;
}

export function MecomRecordingCount({ isRecording, codigoBip, videoRef }: RecordingCountProps) {
  const [isCounting, setIsCounting] = useState(false);
  const [itemCount, setItemCount] = useState(0);
  const [pendingCounts, setPendingCounts] = useState<Array<{ codigoBip: string; itemCount: number }>>([]);
  const isCountingRef = useRef(false);
  const itemCountRef = useRef(0);
  const activeCodigoBipRef = useRef<string | null>(null);
  const wasRecordingRef = useRef(false);
  const motionCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const previousMotionFrameRef = useRef<Uint8ClampedArray | null>(null);
  const motionFrameRequestRef = useRef<number | null>(null);
  const motionFrameModeRef = useRef<"video" | "animation" | null>(null);
  const lastMotionAnalysisRef = useRef(0);
  const motionActiveRef = useRef(false);
  const lastMotionAtRef = useRef(0);
  const lastCountAtRef = useRef(0);
  const scheduleMotionFrameRef = useRef<() => void>(() => {});
  const { mutateAsync: saveRecordingCount } = trpc.mecom.recordingCount.save.useMutation();

  const stopCounting = useCallback((): number | undefined => {
    if (!isCountingRef.current) return undefined;
    isCountingRef.current = false;
    const video = videoRef.current as (HTMLVideoElement & { cancelVideoFrameCallback?: (handle: number) => void }) | null;
    if (motionFrameRequestRef.current !== null) {
      if (motionFrameModeRef.current === "video") video?.cancelVideoFrameCallback?.(motionFrameRequestRef.current);
      if (motionFrameModeRef.current === "animation") cancelAnimationFrame(motionFrameRequestRef.current);
    }
    motionFrameRequestRef.current = null;
    motionFrameModeRef.current = null;
    previousMotionFrameRef.current = null;
    motionActiveRef.current = false;
    setIsCounting(false);
    return itemCountRef.current;
  }, [videoRef]);

  const processMotionFrame = useCallback(() => {
    if (!isCountingRef.current) return;
    const video = videoRef.current;
    if (video && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && video.videoWidth > 0 && video.videoHeight > 0) {
      const now = performance.now();
      if (now - lastMotionAnalysisRef.current >= MOTION_ANALYSIS_INTERVAL_MS) {
        const canvas = motionCanvasRef.current || document.createElement("canvas");
        canvas.width = MOTION_FRAME_WIDTH;
        canvas.height = MOTION_FRAME_HEIGHT;
        motionCanvasRef.current = canvas;
        const context = canvas.getContext("2d", { willReadFrequently: true });
        if (context) {
          context.drawImage(video, 0, 0, MOTION_FRAME_WIDTH, MOTION_FRAME_HEIGHT);
          const currentFrame = context.getImageData(0, 0, MOTION_FRAME_WIDTH, MOTION_FRAME_HEIGHT).data;
          const previousFrame = previousMotionFrameRef.current;
          if (previousFrame) {
            const motionRatio = calculateMotionRatio(currentFrame, previousFrame);
            if (motionRatio >= MOTION_RATIO_THRESHOLD) {
              motionActiveRef.current = true;
              lastMotionAtRef.current = now;
            } else if (
              motionActiveRef.current
              && now - lastMotionAtRef.current >= MOTION_SETTLE_MS
              && now - lastCountAtRef.current >= MIN_COUNT_INTERVAL_MS
            ) {
              itemCountRef.current += 1;
              lastCountAtRef.current = now;
              motionActiveRef.current = false;
              setItemCount(itemCountRef.current);
            }
          }
          previousMotionFrameRef.current = new Uint8ClampedArray(currentFrame);
          lastMotionAnalysisRef.current = now;
        }
      }
    }
    scheduleMotionFrameRef.current();
  }, [videoRef]);

  useEffect(() => {
    scheduleMotionFrameRef.current = () => {
      if (!isCountingRef.current) return;
      const video = videoRef.current as (HTMLVideoElement & { requestVideoFrameCallback?: (callback: () => void) => number }) | null;
      if (video?.requestVideoFrameCallback) {
        motionFrameModeRef.current = "video";
        motionFrameRequestRef.current = video.requestVideoFrameCallback(processMotionFrame);
      } else {
        motionFrameModeRef.current = "animation";
        motionFrameRequestRef.current = requestAnimationFrame(processMotionFrame);
      }
    };
  }, [processMotionFrame, videoRef]);

  const startCounting = useCallback(() => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0 || video.videoHeight === 0) return;
    itemCountRef.current = 0;
    previousMotionFrameRef.current = null;
    lastMotionAnalysisRef.current = 0;
    motionActiveRef.current = false;
    lastMotionAtRef.current = 0;
    lastCountAtRef.current = 0;
    isCountingRef.current = true;
    setItemCount(0);
    setIsCounting(true);
    scheduleMotionFrameRef.current();
  }, [videoRef]);

  useEffect(() => {
    if (isRecording && codigoBip) {
      activeCodigoBipRef.current = codigoBip;
      wasRecordingRef.current = true;
      return;
    }
    if (!isRecording && wasRecordingRef.current) {
      wasRecordingRef.current = false;
      const completedCodigoBip = activeCodigoBipRef.current;
      const completedCount = stopCounting();
      activeCodigoBipRef.current = null;
      if (completedCodigoBip && completedCount !== undefined) {
        setPendingCounts((current) => [
          ...current.filter((item) => item.codigoBip !== completedCodigoBip),
          { codigoBip: completedCodigoBip, itemCount: completedCount },
        ]);
      }
    }
  }, [codigoBip, isRecording, stopCounting]);

  useEffect(() => {
    if (pendingCounts.length === 0) return;
    const pendingCount = pendingCounts[0];
    let cancelled = false;
    let retryTimer: number | undefined;
    const retryStartedAt = Date.now();

    const saveAfterOriginalRecording = async () => {
      try {
        const result = await saveRecordingCount(pendingCount);
        if (cancelled) return;
        if (result.saved) {
          setPendingCounts((current) => current.filter((item) => item.codigoBip !== pendingCount.codigoBip));
          return;
        }
      } catch {
        if (cancelled) return;
      }
      if (Date.now() - retryStartedAt < MAX_SAVE_RETRY_MS) {
        retryTimer = window.setTimeout(() => { void saveAfterOriginalRecording(); }, 1000);
      }
    };

    void saveAfterOriginalRecording();
    return () => {
      cancelled = true;
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
    };
  }, [pendingCounts, saveRecordingCount]);

  useEffect(() => () => { stopCounting(); }, [stopCounting]);

  if (!isRecording || !codigoBip) return null;

  return (
    <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded">
      {!isCounting ? (
        <Button type="button" onClick={startCounting}>Iniciar contagem</Button>
      ) : (
        <span className="text-sm font-medium text-blue-700">Contagem em andamento</span>
      )}
      <span className="text-sm font-medium ml-auto">{itemCount} peças</span>
    </div>
  );
}

type RecordingCounts = Record<string, number | null>;
const RecordingCountsContext = createContext<RecordingCounts>({});

export function MecomRecordingCountProvider({ codigoBips, children }: { codigoBips: string[]; children: React.ReactNode }) {
  const uniqueCodes = useMemo(() => [...new Set(codigoBips.filter(Boolean))], [codigoBips]);
  const { data } = trpc.mecom.recordingCount.list.useQuery(
    { codigoBips: uniqueCodes },
    { enabled: uniqueCodes.length > 0, refetchInterval: 5000 }
  );
  const counts = useMemo<RecordingCounts>(() => Object.fromEntries((data || []).map((item: { codigoBip: string; gravacaoContagem: number | null }) => [item.codigoBip, item.gravacaoContagem])), [data]);
  return <RecordingCountsContext.Provider value={counts}>{children}</RecordingCountsContext.Provider>;
}

export function MecomRecordingCountHeader() {
  return <th className="p-2 text-left">Contagem</th>;
}

export function MecomRecordingCountCell({ codigoBip }: { codigoBip: string }) {
  const counts = useContext(RecordingCountsContext);
  const count = counts[codigoBip];
  return <td className="p-2">{count === null || count === undefined ? "-" : `${count} peças`}</td>;
}
